/**
 * useReferenceData.js
 * -------------------
 * React hook that fetches canonical dropdown values from the ReferenceData
 * container via the get_reference_data Azure Function.
 *
 * Features:
 *  - Fetches multiple fields in a single API call
 *  - Site-aware: pass siteId to get global + site-specific values merged
 *  - Module-level cache: values are shared across all component instances
 *    within a session — no redundant fetches
 *  - addValue(): calls update_reference_data to persist a new combo box entry,
 *    then updates local state immediately (optimistic)
 *
 * Usage:
 *   const { options, loading, addValue } = useReferenceData(
 *     ['tool_type', 'oem', 'site_location'],
 *     callerEmail,
 *     siteId          // optional — required for site-scoped fields
 *   );
 *
 *   // In JSX:
 *   options.tool_type   // ['STAMPING DIE', 'MOLD', ...]
 *   options.site_location  // site-specific values for this site
 *
 *   // When field rep types a new location value and saves:
 *   await addValue('site_location', 'Row 8', siteId);
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
// Module-level cache — persists for the lifetime of the browser session.
// Key: "{fields_sorted}:{site_id}" → { options, fetchedAt }
const cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function cacheKey(fields, siteId) {
  return `${[...fields].sort().join(',')}:${siteId || 'global'}`;
}

export function useReferenceData(fields = [], callerEmail = null, siteId = null) {
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Stable ref so the fetch effect doesn't re-run on every render
  const fieldsKey = fields.slice().sort().join(',');
  const prevKey   = useRef(null);

  useEffect(() => {
    if (fields.length === 0) {
      setLoading(false);
      return;
    }

    const key = cacheKey(fields, siteId);

    // Return cached result if still fresh
    if (cache.has(key)) {
      const cached = cache.get(key);
      if (Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        setOptions(cached.options);
        setLoading(false);
        return;
      }
    }

    // Avoid duplicate fetches when fieldsKey + siteId haven't changed
    // (but allow re-fetch if cache was stale — cleared above)
    if (prevKey.current === key && cache.has(key)) return;
    prevKey.current = key;

    async function fetchOptions() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ fields: fields.join(',') });
        if (callerEmail) params.append('caller_email', callerEmail); // fallback-window param — drop at Phase 4
        if (siteId) params.append('site_id', siteId);

        const response = await apiFetch(`${API_BASE_URL}/get_reference_data?${params}`);
        if (!response.ok) throw new Error(`get_reference_data failed: ${response.status}`);

        const data = await response.json();
        cache.set(key, { options: data, fetchedAt: Date.now() });
        setOptions(data);
      } catch (err) {
        console.error('useReferenceData fetch error:', err);
        const stale = cache.get(key); // offline/failure resilience: last-known values beat empty dropdowns
        if (stale) setOptions(stale.options);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsKey, callerEmail, siteId]);

  /**
   * addValue — appends a new value to a field's list.
   * Updates local state immediately (optimistic), then persists to backend.
   * For site-scoped fields, pass the siteId.
   *
   * @param {string} fieldName
   * @param {string} value
   * @param {string|null} fieldSiteId — override siteId for this specific field
   */
  const addValue = useCallback(async (fieldName, value, fieldSiteId = null) => {
    if (!value || !value.trim()) return;

    const trimmed = value.trim();

    // Optimistic update — add to local state immediately
    setOptions(prev => {
      const current = prev[fieldName] || [];
      if (current.includes(trimmed)) return prev; // already present
      return { ...prev, [fieldName]: [...current, trimmed] };
    });

    // Invalidate cache so next mount gets fresh data
    cache.forEach((_, k) => {
      if (k.includes(fieldName)) cache.delete(k);
    });

    // Persist to backend
    try {
      const params = new URLSearchParams();
      if (callerEmail) params.append('caller_email', callerEmail); // fallback-window param — drop at Phase 4
      const body = { field_name: fieldName, value: trimmed };
      if (fieldSiteId || siteId) body.site_id = fieldSiteId || siteId;

      const response = await apiFetch(`${API_BASE_URL}/update_reference_data?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error('useReferenceData addValue error:', err.error || response.status);
        // Note: we leave the optimistic update in place — the value is still
        // usable for this session even if the persist failed.
      }
    } catch (err) {
      console.error('useReferenceData addValue network error:', err);
    }
  }, [callerEmail, siteId]);

  return { options, loading, error, addValue };
}
